import { createClient } from '@supabase/supabase-js';

// Supabase client instance (will be initialized on first use)
let supabaseInstance = null;
let configPromise = null;

// Function to fetch Supabase config from Cloudflare API
async function fetchSupabaseConfig() {
  try {
    const response = await fetch('/api/supabase-config');
    if (!response.ok) {
      throw new Error('Failed to fetch Supabase configuration');
    }
    const config = await response.json();
    
    if (!config.supabaseUrl || !config.supabaseAnonKey) {
      throw new Error('Invalid Supabase configuration received');
    }
    
    return config;
  } catch (error) {
    console.error('Error fetching Supabase config from API:', error);
    
    // Fallback to environment variables (for local development)
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Supabase configuration not available. Please check Cloudflare environment variables.');
    }
    
    return { supabaseUrl, supabaseAnonKey };
  }
}

// Initialize Supabase client
async function initializeSupabase() {
  if (supabaseInstance) {
    return supabaseInstance;
  }
  
  // Fetch config from API
  const config = await fetchSupabaseConfig();
  
  // Create Supabase client
  supabaseInstance = createClient(config.supabaseUrl, config.supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  });
  
  console.log('Supabase client initialized successfully');
  return supabaseInstance;
}

// Get Supabase client (initializes if needed)
export async function getSupabase() {
  if (!configPromise) {
    configPromise = initializeSupabase();
  }
  return configPromise;
}

// Export a wrapper object that delegates to the actual client
// This allows synchronous-looking code while handling async initialization
export const supabase = {
  rpc: async (...args) => {
    const client = await getSupabase();
    return client.rpc(...args);
  },
  from: async (...args) => {
    const client = await getSupabase();
    return client.from(...args);
  },
  auth: {
    signIn: async (...args) => {
      const client = await getSupabase();
      return client.auth.signIn(...args);
    },
    signOut: async (...args) => {
      const client = await getSupabase();
      return client.auth.signOut(...args);
    },
    user: async () => {
      const client = await getSupabase();
      return client.auth.user();
    }
  },
  channel: async (...args) => {
    const client = await getSupabase();
    return client.channel(...args);
  }
};